import Api, { BASE_URL } from "../api/api";

const STORAGE_KEY = "publishing-analytics-visit";
const VISIT_TIMEOUT = 30 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
let memoryVisit = null;
const views = new Map();
export const isPublicEntertainmentPath = path => /^\/avwakinentertainment(?:\/?$|\/(?:videos|shorts|about|sponsors|schedule|community|merch|contact)\/?$|\/video\/[A-Za-z0-9_-]{11}\/?$)/i.test(path);

function enabled() {
  return process.env.REACT_APP_ANALYTICS_ENABLED === "true" && !Api.token &&
    navigator.doNotTrack !== "1" && window.doNotTrack !== "1" && !navigator.globalPrivacyControl &&
    !/^\/(admin|fourh)(\/|$)/i.test(window.location.pathname) &&
    (!/^\/avwakinentertainment(?:\/|$)/i.test(window.location.pathname) || isPublicEntertainmentPath(window.location.pathname)) &&
    (!/^\/avwakindevelopment(?:\/|$)/i.test(window.location.pathname) || /^\/avwakindevelopment\/?$/i.test(window.location.pathname));
}

function uuid() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function label(value) {
  // Restrict attribution to campaign keys, never raw URLs, emails or free text.
  return value && /^[a-z0-9_.-]{1,100}$/i.test(value) ? value : null;
}

function incomingSource() {
  try {
    const referrer = new URL(document.referrer);
    return referrer.hostname === window.location.hostname ? "direct" : label(referrer.hostname);
  } catch { return "direct"; }
}

export function captureVisit() {
  if (!enabled()) return null;
  const now = Date.now();
  let visit = memoryVisit;
  try { visit = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || visit; } catch { /* Storage may be disabled. */ }
  if (!visit || typeof visit.last_seen !== "number" || now - visit.last_seen >= VISIT_TIMEOUT ||
      !UUID.test(visit.session_id || "") || visit.last_seen > now) {
    const params = new URLSearchParams(window.location.search);
    const source = label(params.get("utm_source"));
    visit = {
      session_id: uuid(), traffic_source: source || incomingSource(),
      utm_source: source, utm_medium: label(params.get("utm_medium")),
      utm_campaign: label(params.get("utm_campaign")), last_seen: now,
    };
    views.clear();
  }
  visit.last_seen = now;
  memoryVisit = visit;
  try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(visit)); } catch { /* In-memory visit is sufficient. */ }
  return {
    session_id: visit.session_id, traffic_source: label(visit.traffic_source) || "direct",
    utm_source: label(visit.utm_source), utm_medium: label(visit.utm_medium),
    utm_campaign: label(visit.utm_campaign),
  };
}

export function retailerKey(url) {
  try {
    const host = new URL(url, window.location.origin).hostname.toLowerCase();
    if (/(^|\.)amazon\.(com|co\.uk|ca|com\.au|de|fr|it|es|co\.jp|in|com\.br|com\.mx|nl|se|pl|sg|ae|sa)$/.test(host) || host === "amzn.to") return "amazon";
    if (/(^|\.)barnesandnoble\.com$/.test(host)) return "barnes_and_noble";
    if (/(^|\.)audible\.(com|co\.uk|ca|com\.au|de|fr|it|es|co\.jp|in)$/.test(host)) return "audible";
    if (/(^|\.)kobo\.com$/.test(host)) return "kobo";
    if (host === window.location.hostname) return "website";
  } catch { /* Invalid links are not treated as a known retailer. */ }
  return "other";
}

export function trackEvent(eventType, fields = {}) {
  try {
    const attribution = captureVisit();
    if (!attribution) return;
    const event = {
      ...attribution, event_id: uuid(), event_type: eventType,
      page: window.location.pathname,
      book_title: fields.book_title || null,
      destination_retailer: fields.destination_retailer || null,
    };
    // Keepalive allows outbound navigation without delaying the user's action.
    // There are no automatic retries; any future retry must reuse event_id.
    Promise.resolve(window.fetch(`${BASE_URL}/api/analytics/events`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event), keepalive: true,
    })).catch(() => {});
  } catch { /* Telemetry must never interrupt a purchase or signup. */ }
}

export function trackBookView(title, navigationKey) {
  if (!enabled()) return;
  captureVisit();
  const key = `${navigationKey}:${title}`;
  if (views.has(key)) return;
  views.set(key, true);
  if (views.size > 200) views.delete(views.keys().next().value);
  trackEvent("book_view", { book_title: title });
}

export function trackDevelopmentView(navigationKey) {
  if (!enabled()) return;
  captureVisit();
  const key = `development:${navigationKey}`;
  if (views.has(key)) return;
  views.set(key, true);
  if (views.size > 200) views.delete(views.keys().next().value);
  trackEvent("development_page_view");
}

export function trackEntertainmentView(navigationKey) {
  if (!enabled() || !isPublicEntertainmentPath(window.location.pathname)) return;
  captureVisit();
  const key = `entertainment:${navigationKey}:${window.location.pathname}`;
  if (views.has(key)) return;
  views.set(key, true);
  if (views.size > 200) views.delete(views.keys().next().value);
  trackEvent("entertainment_page_view");
}
